import { createAction, props } from '@ngrx/store';
import {
  WorkElement,
  WorkElementDetailsPayload,
  WorkThread,
  WorkRouteQueryState,
  WorkSearchScope,
  IEBWorkTreeNode,
} from './work.models';
import { KanbanStatus } from '@shared/models/kanban-status.enum';

export const enterWorkRoute = createAction(
  '[Work] Enter Route',
  props<{
    elementId: string;
    elementType: string;
    query: WorkRouteQueryState;
  }>(),
);

export const navigateUp = createAction('[Work] Navigate Up');

export const openChild = createAction(
  '[Work] Open Child',
  props<{ childId: string; childType: string }>(),
);

export const selectChild = createAction(
  '[Work] Select Child',
  props<{ childId: string | null; childType?: string }>(),
);

export const toggleNodeExpanded = createAction(
  '[Work] Toggle Node Expanded',
  props<{ nodeId: string; expanded: boolean }>(),
);

export const setExpandedNodeIds = createAction(
  '[Work] Set Expanded Node Ids',
  props<{ nodeIds: string[] }>(),
);

export const setQuickFilter = createAction(
  '[Work] Set Quick Filter',
  props<{ statuses: KanbanStatus[] }>(),
);

export const setTagFilter = createAction(
  '[Work] Set Tag Filter',
  props<{ tagIds: string[] }>(),
);

export const setSearchTerm = createAction(
  '[Work] Set Search Term',
  props<{ term: string }>(),
);

export const setSearchScope = createAction(
  '[Work] Set Search Scope',
  props<{ scope: WorkSearchScope }>(),
);

export const setActiveTab = createAction(
  '[Work] Set Active Tab',
  props<{ tabKey: string | null }>(),
);

// Simple, stateless preview request from tree click
export const previewRequested = createAction(
  '[Work] Preview Requested',
  props<{ id: string; elementType: string; title?: string }>(),
);

export const loadElement = createAction(
  '[Work] Load Element',
  props<{ id: string; elementType: string }>(),
);

export const loadElementSuccess = createAction(
  '[Work] Load Element Success',
  props<{ element: WorkElement }>(),
);

export const loadElementFailure = createAction(
  '[Work] Load Element Failure',
  props<{ id: string; error: unknown }>(),
);

export const loadChildren = createAction(
  '[Work] Load Children',
  props<{ parentId: string; parentType: string }>(),
);

export const loadChildrenSuccess = createAction(
  '[Work] Load Children Success',
  props<{ parentId: string; children: WorkElement[] }>(),
);

export const loadChildrenFailure = createAction(
  '[Work] Load Children Failure',
  props<{ parentId: string; error: unknown }>(),
);

export const loadDetails = createAction(
  '[Work] Load Details',
  props<{ elementId: string; elementType: string }>(),
);

export const loadDetailsSuccess = createAction(
  '[Work] Load Details Success',
  props<{ elementId: string; details: WorkElementDetailsPayload }>(),
);

export const loadDetailsFailure = createAction(
  '[Work] Load Details Failure',
  props<{ elementId: string; error: unknown }>(),
);

export const loadThreads = createAction(
  '[Work] Load Threads',
  props<{ elementId: string; elementType: string }>(),
);

export const loadThreadsSuccess = createAction(
  '[Work] Load Threads Success',
  props<{ elementId: string; threads: WorkThread[] }>(),
);

export const loadThreadsFailure = createAction(
  '[Work] Load Threads Failure',
  props<{ elementId: string; error: unknown }>(),
);

export const createThread = createAction(
  '[Work] Create Thread',
  props<{ elementId: string; elementType: string; title?: string | null; message: string }>(),
);

export const createThreadSuccess = createAction(
  '[Work] Create Thread Success',
  props<{ elementId: string; thread: WorkThread }>(),
);

export const createThreadFailure = createAction(
  '[Work] Create Thread Failure',
  props<{ elementId: string; error: unknown }>(),
);

export const createChildRequested = createAction(
  '[Work] Create Child Requested',
  props<{ parentId: string; elementType: string; payload: Partial<WorkElement> }>(),
);

export const createChildSuccess = createAction(
  '[Work] Create Child Success',
  props<{ parentId: string; child: WorkElement }>(),
);

export const createChildFailure = createAction(
  '[Work] Create Child Failure',
  props<{ parentId: string; error: unknown }>(),
);

export const setPartyTree = createAction(
  '[Work] Set Party Tree',
  props<{ partyId: string; tree: IEBWorkTreeNode }>(),
);

/** Sets ONLY the full party tree for navigation - does NOT update display tree */
export const setFullPartyTree = createAction(
  '[Work] Set Full Party Tree',
  props<{ partyId: string; tree: IEBWorkTreeNode }>(),
);

export const setElementTree = createAction(
  '[Work] Set Element Tree',
  props<{ elementId: string; elementType: string; tree: IEBWorkTreeNode }>(),
);

export const setTreeLoading = createAction(
  '[Work] Set Tree Loading',
  props<{ parentId: string; loading: boolean }>(),
);

export const setTreeError = createAction(
  '[Work] Set Tree Error',
  props<{ parentId: string; error: unknown }>(),
);

export const hydrateUiFromRoute = createAction(
  '[Work] Hydrate UI From Route',
  props<{ query: WorkRouteQueryState }>(),
);

export const resetWorkState = createAction('[Work] Reset');
